import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const FeedbackForm = ({ item, onDone }) => {
  const [strengths, setStrengths] = useState("");
  const [growth, setGrowth] = useState("");
  const [rating, setRating] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/reviews/feedback/${item.id}`, {
        strengths, growth_areas: growth, collaboration_rating: Number(rating),
      });
      toast.success(`Feedback submitted for ${item.subject_name}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 space-y-3 bg-slate-50 border-t border-border">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Strengths</p>
          <Textarea data-testid={`fb-strengths-${item.subject_id}`} value={strengths} rows={3}
            onChange={(e) => setStrengths(e.target.value)} placeholder="What does this colleague do well?"
            className="rounded-none text-[13px] bg-white" />
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Growth areas</p>
          <Textarea data-testid={`fb-growth-${item.subject_id}`} value={growth} rows={3}
            onChange={(e) => setGrowth(e.target.value)} placeholder="Where could they improve?"
            className="rounded-none text-[13px] bg-white" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-slate-600">Collaboration rating</span>
          <Select value={rating} onValueChange={setRating}>
            <SelectTrigger className="rounded-none w-24 h-8 text-[12px] bg-white" data-testid={`fb-rating-${item.subject_id}`}>
              <SelectValue placeholder="1-5" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)} className="text-[12px]">{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={submit} disabled={busy || !strengths.trim() || !growth.trim() || !rating}
          data-testid={`fb-submit-${item.subject_id}`}
          className="rounded-none h-8 text-[12px] bg-teal-600 hover:bg-teal-700">
          {busy ? "Submitting…" : "Submit feedback"}
        </Button>
      </div>
    </div>
  );
};

export const MyFeedback = () => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/reviews/my/feedback").then((r) => setData(r.data)).catch(() => setData({ assignments: [] }));
  useEffect(() => { load(); }, []);

  if (!data) return <Skeleton className="h-80 w-full rounded-none" />;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="my-feedback">
      <p className="text-[12px] text-muted-foreground">
        {data.pending > 0
          ? `${data.pending} peer feedback request(s) awaiting your input. Your feedback feeds the AI synthesis anonymously.`
          : "No pending feedback requests — thank you."}
      </p>
      <div className="bg-white border border-border divide-y divide-border">
        {data.assignments.length === 0 && (
          <p className="px-4 py-10 text-center text-[13px] text-muted-foreground">No feedback assignments in active cycles.</p>
        )}
        {data.assignments.map((a) => (
          <div key={a.id} data-testid={`feedback-item-${a.subject_id}`}>
            <div className="flex items-center px-4 py-3 gap-4">
              <div className="flex-1">
                <p className="text-[13.5px] font-medium text-slate-800">{a.subject_name}</p>
                <p className="text-[11px] text-muted-foreground">{a.subject_title}</p>
              </div>
              {a.status === "submitted" ? (
                <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-teal-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> SUBMITTED · rated {a.collaboration_rating}/5
                </span>
              ) : (
                <Button size="sm" variant="outline" data-testid={`fb-open-${a.subject_id}`}
                  onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                  className="rounded-none h-8 text-[11px] border-teal-600/50 text-teal-700 hover:bg-teal-50">
                  {expanded === a.id ? "Close" : "Give feedback"}
                </Button>
              )}
            </div>
            {expanded === a.id && a.status !== "submitted" && (
              <FeedbackForm item={a} onDone={() => { setExpanded(null); load(); }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyFeedback;
